// templateModel.ts — all the non-visual pieces of the template maker:
// the segment model + serialisation, the personalization field catalog, the
// ready-made starters, and the language list.
//
// The message body is an ordered list of Segments rather than a raw "{{1}}" string,
// so the customer never sees braces. buildPayload() serialises it back into the EXACT
// payload the existing backend already accepts — the backend is unchanged.
//
// Payload parity: for NAMED the backend reads component.example as an array of
// { param_name, example } and turns it into Meta's body_text_named_params; for
// POSITIONAL it expects an ordered array of example strings (see backend
// whatsapp.ts createWhatsAppTemplate).

/* ============================ Segment model ============================ */

export type Category = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'
export type ParameterFormat = 'NAMED' | 'POSITIONAL'
export type RenderMode = 'preview' | 'meta'

/* ============================ Buttons ============================ */
// The backend (whatsapp.ts createWhatsAppTemplate) already accepts a BUTTONS component
// with QUICK_REPLY / URL / PHONE_NUMBER / FLOW and forwards it verbatim to Meta. The UI
// builds the first three here; FLOW is added alongside Flow creation (needs a flow picker).
export type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'

export interface TemplateButton {
  id: string // unique per row (UI only)
  type: ButtonType
  text: string // the tappable label (all types)
  url?: string // URL type only
  phoneNumber?: string // PHONE_NUMBER type only
}

export const BUTTON_TYPE_OPTIONS: { label: string; value: ButtonType }[] = [
  { label: 'Quick reply', value: 'QUICK_REPLY' },
  { label: 'Visit website', value: 'URL' },
  { label: 'Call phone number', value: 'PHONE_NUMBER' }
]

// Meta caps: 10 buttons total, at most 2 URL and 1 phone; button text ≤ 25 chars.
export const MAX_BUTTONS = 10
export const MAX_URL_BUTTONS = 2
export const MAX_PHONE_BUTTONS = 1
export const MAX_BUTTON_TEXT = 25

let buttonSeq = 0
export function makeButton(type: ButtonType = 'QUICK_REPLY'): TemplateButton {
  buttonSeq += 1
  return {
    id: `btn_${buttonSeq}_${Math.random().toString(36).slice(2, 7)}`,
    type,
    text: '',
    url: '',
    phoneNumber: ''
  }
}

export interface TextSegment {
  type: 'text'
  value: string
}

export interface FieldSegment {
  type: 'field'
  id: string // unique per inserted pill instance
  key: string // canonical field key, e.g. 'first_name' or 'custom.order_id'
  label: string // friendly label shown inside the pill, e.g. 'First name'
  fallback: string // "if this person has no value, show this instead"
  sample: string // sample value used for the live preview + Meta example
}

export type Segment = TextSegment | FieldSegment

export interface TemplateDraft {
  name: string
  category: Category
  language: string
  headerEnabled: boolean
  headerText: string
  bodySegments: Segment[]
  footerText: string
  // NAMED ({{first_name}}) is the default and auto-fills from contacts/sheet columns
  // by name; POSITIONAL ({{1}}) is the opt-in escape hatch. Pills look identical
  // either way — only serialisation changes.
  parameterFormat: ParameterFormat
  // Optional call-to-action / quick-reply buttons shown under the message.
  buttons: TemplateButton[]
}

// Opt-out language is expected on WhatsApp marketing templates, so default it rather
// than making the user remember it. Still fully editable.
export const DEFAULT_FOOTER = 'Reply STOP to opt out'

export function emptyDraft(): TemplateDraft {
  return {
    name: '',
    category: 'MARKETING',
    language: 'en',
    headerEnabled: false,
    headerText: '',
    bodySegments: [{ type: 'text', value: '' }],
    footerText: DEFAULT_FOOTER,
    parameterFormat: 'NAMED',
    buttons: []
  }
}

/* ====================== Personalization field catalog ====================== */

export interface FieldDef {
  key: string
  label: string
  group: string
  icon: string // maps to a heroicon in PillEditor's FieldIcon
  sample: string
  fallback: string
}

export const CONTACT_GROUP = 'From your contacts'
export const CUSTOM_GROUP = 'Custom details'

// Mirrors the CRM Contact model (firstName, lastName, phone, email, address.*) so
// these auto-fill for every recipient.
export const STANDARD_FIELDS: FieldDef[] = [
  { key: 'first_name', label: 'First name', group: CONTACT_GROUP, icon: 'user', sample: 'Priya', fallback: 'there' },
  { key: 'last_name', label: 'Last name', group: CONTACT_GROUP, icon: 'user', sample: 'Sharma', fallback: '' },
  { key: 'phone', label: 'Phone', group: CONTACT_GROUP, icon: 'phone', sample: '+91 98765 43210', fallback: '' },
  { key: 'email', label: 'Email', group: CONTACT_GROUP, icon: 'envelope', sample: 'priya@email.com', fallback: '' },
  { key: 'city', label: 'City', group: CONTACT_GROUP, icon: 'mappin', sample: 'Mumbai', fallback: '' },
  { key: 'state', label: 'State', group: CONTACT_GROUP, icon: 'mappin', sample: 'Maharashtra', fallback: '' },
  { key: 'country', label: 'Country', group: CONTACT_GROUP, icon: 'mappin', sample: 'India', fallback: '' }
]

// A few ready-made custom details so the "Custom" group is never empty and users see
// what "custom" means. They can still coin their own.
export const SUGGESTED_CUSTOM_FIELDS: FieldDef[] = [
  { key: 'custom.order_id', label: 'Order ID', group: CUSTOM_GROUP, icon: 'tag', sample: '#4821', fallback: '' },
  { key: 'custom.amount', label: 'Amount', group: CUSTOM_GROUP, icon: 'tag', sample: '₹1,499', fallback: '' },
  { key: 'custom.date', label: 'Date', group: CUSTOM_GROUP, icon: 'calendar', sample: '12 Aug', fallback: '' },
  { key: 'custom.appointment_time', label: 'Appointment time', group: CUSTOM_GROUP, icon: 'calendar', sample: '4:30 PM', fallback: '' }
]

export function defaultCatalog(): FieldDef[] {
  return [...STANDARD_FIELDS, ...SUGGESTED_CUSTOM_FIELDS]
}

// Build a FieldDef for a brand-new custom detail the user typed a label for.
export function makeCustomField(label: string): FieldDef {
  const trimmed = label.trim()
  const key =
    'custom.' +
    trimmed
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
  return { key, label: trimmed, group: CUSTOM_GROUP, icon: 'tag', sample: '', fallback: '' }
}

/* ========================== Serialisation ========================== */

// Convert a field key into a Meta-valid named variable ( ^[a-z0-9_]+$ ).
// 'custom.order_id' -> 'order_id', 'First Name' -> 'first_name'.
export function toVariableName(key: string): string {
  const cleaned = key
    .replace(/^custom\./, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return cleaned || 'field'
}

// Render the body segments to a string.
//  - 'preview' substitutes each pill with its sample (then fallback, then label) so
//    the user sees a realistic message.
//  - 'meta' substitutes "{{variable_name}}" (NAMED) or "{{1}}", "{{2}}" in document
//    order (POSITIONAL) for the API payload.
export function segmentsToText(
  segments: Segment[],
  mode: RenderMode,
  format: ParameterFormat = 'NAMED'
): string {
  if (mode === 'meta' && format === 'POSITIONAL') {
    let n = 0
    return segments.map(seg => (seg.type === 'text' ? seg.value : `{{${(n += 1)}}}`)).join('')
  }
  return segments
    .map(seg => {
      if (seg.type === 'text') return seg.value
      if (mode === 'meta') return `{{${toVariableName(seg.key)}}}`
      return seg.sample || seg.fallback || seg.label
    })
    .join('')
}

export interface CollectedVariable {
  name: string // meta variable name
  label: string
  sample: string
  fallback: string
}

// Unique variables in first-appearance order (Meta dedupes named params by name).
export function collectVariables(segments: Segment[]): CollectedVariable[] {
  const seen = new Map<string, CollectedVariable>()
  for (const seg of segments) {
    if (seg.type !== 'field') continue
    const name = toVariableName(seg.key)
    if (seen.has(name)) continue
    seen.set(name, {
      name,
      label: seg.label,
      sample: seg.sample || seg.fallback || seg.label,
      fallback: seg.fallback
    })
  }
  return [...seen.values()]
}

export function slugifyTemplateName(name: string): string {
  // Mirrors the backend (lowercase + whitespace to underscores) but also strips
  // anything Meta disallows, so what we show matches what is actually created.
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/* ============================ Validation ============================ */

export interface ValidationResult {
  ok: boolean
  errors: Record<string, string>
  messages: string[] // human-friendly nudges for a summary area
}

// Meta's structural rules enforced silently, so "Submit" only lights up when the
// template will actually be accepted — the user never has to learn them.
export function validateDraft(draft: TemplateDraft): ValidationResult {
  const errors: Record<string, string> = {}
  const messages: string[] = []

  if (!draft.name.trim()) {
    errors.name = 'Give your template a name'
  } else if (!slugifyTemplateName(draft.name)) {
    errors.name = 'Use at least one letter or number in the name'
  }

  if (!segmentsToText(draft.bodySegments, 'preview').trim()) errors.body = 'Write your message'

  if (draft.headerEnabled && !draft.headerText.trim()) {
    errors.header = 'Add header text or turn the header off'
  }

  // Meta rejects two variables with no text between them.
  const meaningful = draft.bodySegments.filter(
    s => s.type === 'field' || (s.type === 'text' && s.value.length > 0)
  )
  for (let i = 0; i < meaningful.length - 1; i++) {
    if (meaningful[i].type === 'field' && meaningful[i + 1].type === 'field') {
      messages.push('Add a few words between two personalizations — WhatsApp needs them separated.')
      errors.body = errors.body || 'Separate adjacent personalizations with text'
      break
    }
  }

  const missingSample = collectVariables(draft.bodySegments).find(v => !v.sample)
  if (missingSample) {
    // Rare because samples are auto-derived, but guard anyway.
    errors.samples = `Add a sample value for "${missingSample.label}"`
  }

  // Button rules
  const buttons = draft.buttons || []
  if (buttons.length > MAX_BUTTONS) {
    errors.buttons = `Use at most ${MAX_BUTTONS} buttons`
  }
  let urlCount = 0
  let phoneCount = 0
  for (const b of buttons) {
    if (!b.text.trim()) {
      errors.buttons = errors.buttons || 'Give every button a label'
      continue
    }
    if (b.text.length > MAX_BUTTON_TEXT) {
      errors.buttons = errors.buttons || `Button text must be ${MAX_BUTTON_TEXT} characters or fewer`
    }
    if (b.type === 'URL') {
      urlCount++
      if (!b.url || !/^https?:\/\/.+/i.test(b.url.trim())) {
        errors.buttons = errors.buttons || 'Add a valid website URL (https://…) for the website button'
      }
    }
    if (b.type === 'PHONE_NUMBER') {
      phoneCount++
      if (!b.phoneNumber || !/^\+?[0-9]{6,15}$/.test(b.phoneNumber.replace(/[\s-]/g, ''))) {
        errors.buttons = errors.buttons || 'Add a valid phone number for the call button'
      }
    }
  }
  if (urlCount > MAX_URL_BUTTONS) {
    errors.buttons = errors.buttons || `WhatsApp allows at most ${MAX_URL_BUTTONS} website buttons`
  }
  if (phoneCount > MAX_PHONE_BUTTONS) {
    errors.buttons = errors.buttons || `WhatsApp allows at most ${MAX_PHONE_BUTTONS} call button`
  }

  return { ok: Object.keys(errors).length === 0, errors, messages }
}

/* ============================ Payload ============================ */

export function buildPayload(draft: TemplateDraft) {
  const components: any[] = []
  const format: ParameterFormat = draft.parameterFormat || 'NAMED'

  if (draft.headerEnabled && draft.headerText.trim()) {
    components.push({ type: 'HEADER', format: 'TEXT', text: draft.headerText.trim() })
  }

  const bodyComponent: any = {
    type: 'BODY',
    text: segmentsToText(draft.bodySegments, 'meta', format).trim()
  }

  const fields = draft.bodySegments.filter(s => s.type === 'field') as FieldSegment[]
  if (fields.length > 0) {
    if (format === 'POSITIONAL') {
      // One example per placeholder, in document order (not deduped).
      bodyComponent.example = fields.map(f => f.sample || f.fallback || f.label)
    } else {
      bodyComponent.example = collectVariables(draft.bodySegments).map(v => ({
        param_name: v.name,
        example: v.sample
      }))
    }
  }

  components.push(bodyComponent)

  if (draft.footerText.trim()) {
    components.push({ type: 'FOOTER', text: draft.footerText.trim() })
  }

  // BUTTONS component — serialised to Meta's exact button shapes. Empty-label rows are
  // dropped. The backend already accepts this component and forwards it to Meta.
  const buttons = (draft.buttons || []).filter(b => b.text.trim())
  if (buttons.length > 0) {
    components.push({
      type: 'BUTTONS',
      buttons: buttons.map(b => {
        if (b.type === 'URL') {
          return { type: 'URL', text: b.text.trim(), url: (b.url || '').trim() }
        }
        if (b.type === 'PHONE_NUMBER') {
          return { type: 'PHONE_NUMBER', text: b.text.trim(), phone_number: (b.phoneNumber || '').trim() }
        }
        return { type: 'QUICK_REPLY', text: b.text.trim() }
      })
    })
  }

  return {
    name: slugifyTemplateName(draft.name),
    category: draft.category,
    language: draft.language,
    parameter_format: format,
    components
  }
}

/* ======================= Draft controller contract =======================
   Declared here (not in the hook) so the UI components can type against it
   without importing the dialog — avoids a circular import. */

export interface MetaError {
  title: string
  message: string
}

export interface TemplateDraftController {
  draft: TemplateDraft
  patch: (p: Partial<TemplateDraft>) => void
  setBody: (segments: Segment[]) => void
  reset: (next?: TemplateDraft) => void
  catalog: FieldDef[]
  registerCustom: (label: string) => FieldDef
  validation: ValidationResult
  submitting: boolean
  submit: () => Promise<boolean>
  metaError: MetaError | null
  setMetaError: (e: MetaError | null) => void
}

/* ============================ Starters ============================ */

export interface StarterField {
  key: string
  fallback?: string
}

export type RawSegment = string | StarterField

export interface Starter {
  id: string
  title: string
  emoji: string
  category: Category
  blurb: string
  name: string
  header?: string
  footer?: string
  body: RawSegment[]
}

// Ready-made templates with personalizations already placed, so a non-technical user
// picks the closest match and tweaks a few words instead of facing a blank box.
export const STARTERS: Starter[] = [
  {
    id: 'order_confirmation',
    title: 'Order confirmation',
    emoji: '📦',
    category: 'UTILITY',
    blurb: 'Confirm an order right after checkout.',
    name: 'Order Confirmation',
    // Meta rejects emoji in a TEXT header; kept emoji-free to guarantee approval.
    header: 'Order confirmed',
    footer: 'Thank you for shopping with us',
    body: [
      'Hi ',
      { key: 'first_name' },
      ', your order ',
      { key: 'custom.order_id' },
      ' is confirmed. We’ll deliver it to ',
      { key: 'city' },
      ' and message you as soon as it ships.'
    ]
  },
  {
    id: 'appointment_reminder',
    title: 'Appointment reminder',
    emoji: '🗓️',
    category: 'UTILITY',
    blurb: 'Remind a customer about an upcoming booking.',
    name: 'Appointment Reminder',
    body: [
      'Hi ',
      { key: 'first_name' },
      ', this is a reminder for your appointment on ',
      { key: 'custom.date' },
      ' at ',
      { key: 'custom.appointment_time' },
      '. See you soon!'
    ]
  },
  {
    id: 'delivery_update',
    title: 'Delivery update',
    emoji: '🚚',
    category: 'UTILITY',
    blurb: 'Tell a customer their order is on the way.',
    name: 'Delivery Update',
    body: [
      'Good news ',
      { key: 'first_name' },
      '! Your order ',
      { key: 'custom.order_id' },
      ' is out for delivery to ',
      { key: 'city' },
      ' today.'
    ]
  },
  {
    id: 'payment_reminder',
    title: 'Payment reminder',
    emoji: '💳',
    category: 'UTILITY',
    blurb: 'Nudge a customer about a pending payment.',
    name: 'Payment Reminder',
    footer: 'Ignore if already paid',
    body: [
      'Hi ',
      { key: 'first_name' },
      ', a gentle reminder that ',
      { key: 'custom.amount' },
      ' is due for order ',
      { key: 'custom.order_id' },
      '. Tap below to pay securely.'
    ]
  },
  {
    id: 'welcome_offer',
    title: 'Welcome offer',
    emoji: '🎁',
    category: 'MARKETING',
    blurb: 'Greet a new customer with a discount.',
    name: 'Welcome Offer',
    header: 'Welcome to the family!',
    body: [
      'Hey ',
      { key: 'first_name' },
      ', welcome! Use code WELCOME10 for 10% off your first order. We’re thrilled to serve you in ',
      { key: 'city' },
      ' and hope you enjoy shopping with us.'
    ]
  },
  {
    id: 'feedback_request',
    title: 'Feedback request',
    emoji: '⭐',
    category: 'MARKETING',
    blurb: 'Ask a happy customer for a quick review.',
    name: 'Feedback Request',
    body: [
      'Hi ',
      { key: 'first_name' },
      ', hope you loved your recent order! Could you share a quick rating? It really helps us.'
    ]
  },
  {
    id: 'abandoned_cart',
    title: 'Abandoned cart',
    emoji: '🛒',
    category: 'MARKETING',
    blurb: 'Nudge someone who left items behind.',
    name: 'Abandoned Cart',
    body: [
      'Hi ',
      { key: 'first_name' },
      ', you left something in your cart! Complete your order now and we’ll deliver it to ',
      { key: 'city' },
      ' in no time.'
    ]
  },
  {
    id: 'booking_confirmed',
    title: 'Booking confirmed',
    emoji: '✅',
    category: 'UTILITY',
    blurb: 'Confirm a booking or reservation.',
    name: 'Booking Confirmed',
    body: [
      'Hi ',
      { key: 'first_name' },
      ', your booking for ',
      { key: 'custom.date' },
      ' at ',
      { key: 'custom.appointment_time' },
      ' is confirmed. See you then!'
    ]
  }
]

let starterSeq = 0
function nextId(): string {
  starterSeq += 1
  return `seg_${starterSeq}_${Math.random().toString(36).slice(2, 7)}`
}

function resolveField(key: string, catalog: FieldDef[]): FieldDef {
  return (
    catalog.find(f => f.key === key) ||
    makeCustomField(key.replace(/^custom\./, '').replace(/_/g, ' '))
  )
}

// Expand a Starter into a fresh editable draft.
export function loadStarter(starter: Starter): TemplateDraft {
  const catalog = defaultCatalog()
  const bodySegments: Segment[] = starter.body.map(raw => {
    if (typeof raw === 'string') return { type: 'text', value: raw }
    const def = resolveField(raw.key, catalog)
    return {
      type: 'field',
      id: nextId(),
      key: def.key,
      label: def.label,
      fallback: raw.fallback ?? def.fallback,
      sample: def.sample
    }
  })

  return {
    ...emptyDraft(),
    name: starter.name,
    category: starter.category,
    language: 'en',
    headerEnabled: !!starter.header,
    headerText: starter.header || '',
    bodySegments,
    // Starters keep their own footer if they define one; otherwise the standard
    // opt-out line.
    footerText: starter.footer || DEFAULT_FOOTER
  }
}

/* ============================ Languages ============================ */

export const WHATSAPP_LANGUAGES: Record<string, string> = {
  af_ZA: 'Afrikaans',
  ar: 'Arabic',
  bg: 'Bulgarian',
  ca: 'Catalan',
  cs: 'Czech',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  en_GB: 'English (UK)',
  en_US: 'English (US)',
  es: 'Spanish',
  es_ES: 'Spanish (Spain)',
  es_MX: 'Spanish (Mexico)',
  fa: 'Persian',
  fi: 'Finnish',
  fr: 'French',
  gu: 'Gujarati',
  he: 'Hebrew',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  kn: 'Kannada',
  ko: 'Korean',
  ml: 'Malayalam',
  mr: 'Marathi',
  nl: 'Dutch',
  pa: 'Punjabi',
  pl: 'Polish',
  pt_BR: 'Portuguese (Brazil)',
  pt_PT: 'Portuguese (Portugal)',
  ru: 'Russian',
  ta: 'Tamil',
  te: 'Telugu',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  vi: 'Vietnamese',
  zh_CN: 'Chinese (Simplified)',
  zh_TW: 'Chinese (Traditional)'
}

export const LANGUAGE_OPTIONS = Object.entries(WHATSAPP_LANGUAGES).map(([code, label]) => ({
  label,
  value: code
}))
